import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SupportHubStore } from '../support-hub.store';
import { FeedbackVersion } from '../support-hub.models';
import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';

type Role = 'user' | 'super_admin' | 'developer';

interface VersionRow {
  id: number;
  label: string;
  targetDate: string;
  statusName: string;
  colorCode: string;
  sortOrder: number;
}

interface FeaturedVersion {
  key: 'latest' | 'development' | 'staging';
  label: string;
  row: VersionRow | null;
}

interface EnvironmentSummary {
  name: string;
  count: number;
  colorCode: string;
}

type CardVariant = 'default' | 'attention' | 'validation' | 'success';

interface DashboardTicket {
  feedback_id: number;
  title: string;
}

interface DashboardCard {
  label: string;
  count: number;
  info: string;
}

interface DashboardListItem {
  feedback_id: number;
  title: string;
}

interface DashboardList {
  label: string;
  items: DashboardListItem[];
  info: string;
}

interface FeedbackDashboardData {
  cards: DashboardCard[];
  lists: DashboardList[];
}

@Component({
  selector: 'app-support-hub-dashboard',
  standalone: true,
  templateUrl: './support-hub-dashboard.component.html',
  styleUrl: './support-hub-dashboard.component.scss'
})
export class SupportHubDashboardComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private lastLoadedUseCaseId: number | null = null;

  currentSupportHubRole: string = SupportHubStore.ROLE_USER;
  loader = { isLoading: false, message: '' };
  versionsCollapsed = false;
  ticketsCollapsed = false;
  versionRows: VersionRow[] = [];
  ticketDashboard: FeedbackDashboardData = this.createEmptyTicketDashboard();
  selectedCard: DashboardCard | null = null;

  /** Maps dashboard card position (1st..4th) to the drilldown API's `card` query value. */
  private readonly drilldownCardKeys: readonly string[] = ['open', 'clarification', 'validation', 'completed'];
  private readonly drilldownRows = 10;
  private selectedCardIndex: number | null = null;
  drilldownTickets: DashboardTicket[] = [];
  drilldownLoading = false;
  drilldownPage = 1;
  drilldownTotalPages = 1;
  @ViewChild('ticketDialog') private ticketDialog?: ElementRef<HTMLDialogElement>;

  constructor(
    public readonly store: SupportHubStore,
    private readonly router: Router,
    private readonly apiService: MfgappsApiService
  ) {
    this.store.currentUserRoleSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(role => {
      this.currentSupportHubRole = role;
    });
  }

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.store.loadLookup();

    this.store.selectedUseCaseIdSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(useCaseId => {
      void this.handleUseCaseSelection(useCaseId);
    });

    const initialUseCaseId = this.store.selectedUseCaseIdValue ?? this.store.getDefaultUseCaseId();
    if (initialUseCaseId && initialUseCaseId !== this.lastLoadedUseCaseId) {
      this.lastLoadedUseCaseId = initialUseCaseId;
      await this.refresh(initialUseCaseId);
    }
  }

  private async handleUseCaseSelection(useCaseId: number | null | undefined): Promise<void> {
    const nextUseCaseId = useCaseId ?? this.store.getDefaultUseCaseId();
    if (!nextUseCaseId || nextUseCaseId === this.lastLoadedUseCaseId) {
      return;
    }
    this.lastLoadedUseCaseId = nextUseCaseId;
    await this.refresh(nextUseCaseId);
  }

  private async refresh(useCaseId: number): Promise<void> {
    this.closeCardTickets();
    this.setLoader('Loading dashboard...');
    try {
      await Promise.all([
        this.loadVersionRows(useCaseId),
        this.loadTicketDashboard(useCaseId)
      ]);
    }
    finally {
      this.resetLoader();
    }
  }

  private async loadVersionRows(useCaseId: number): Promise<void> {
    try {
      const response = await this.apiService.getFeedbackVersions(useCaseId, 'feedback');
      const versionList: FeedbackVersion[] = Array.isArray(response?.result) ? response.result : [];
      this.versionRows = versionList
        .map(version => this.toVersionRow(version))
        .sort((a, b) => b.sortOrder - a.sortOrder);
    }
    catch {
      this.versionRows = [];
    }
  }

  private toVersionRow(version: FeedbackVersion): VersionRow {
    return {
      id: version.version_id,
      label: version.version_name || '',
      targetDate: this.formatDate(version.target_date, version.created_date),
      statusName: version.version_status || 'Unknown',
      colorCode: this.resolveStatusColor(version.version_status_id),
      sortOrder: version.sortorder ?? 0
    };
  }

  private resolveStatusColor(statusId?: number): string {
    return this.store.versionStatusOptions.find(item => item.statusId === statusId)?.colorCode || '#94a3b8';
  }

  private formatDate(rawDate?: string, fallbackDate?: string): string {
    const primary = this.toDate(rawDate || '');
    if (primary.getTime() !== 0) {
      return primary.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    const fallback = this.toDate(fallbackDate || '');
    if (fallback.getTime() !== 0) {
      return fallback.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    return '';
  }

  private toDate(raw: string): Date {
    if (!raw?.trim()) {
      return new Date(0);
    }
    const trimmed = raw.trim();
    const isoLike = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    const normalized = isoLike.replace(/(\.\d{3})\d+$/, '$1');
    const parsed = new Date(normalized);

    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }

    const dateOnly = trimmed.split(' ')[0];
    const fallback = new Date(`${dateOnly}T00:00:00`);
    return Number.isNaN(fallback.getTime()) ? new Date(0) : fallback;
  }

  private setLoader(message: string): void {
    this.loader.isLoading = true;
    this.loader.message = message;
  }

  private resetLoader(): void {
    this.loader.isLoading = false;
    this.loader.message = '';
  }

  toggleVersionsPanel(): void {
    this.versionsCollapsed = !this.versionsCollapsed;
  }

  toggleTicketsPanel(): void {
    this.ticketsCollapsed = !this.ticketsCollapsed;
  }

  get role(): Role {
    if (this.store.isAdminRoleName(this.currentSupportHubRole)) {
      return 'super_admin';
    }
    if (this.currentSupportHubRole === SupportHubStore.ROLE_DEVELOPER) {
      return 'developer';
    }
    return 'user';
  }

  get subtext(): string {
    if (this.role === 'super_admin') {
      return 'Assign work, set version, manage priority, and oversee the queue.';
    }
    if (this.role === 'developer') {
      return 'Focus on assigned work, private notes, status updates, and blockers.';
    }
    return 'Track requests, validate releases, and follow progress.';
  }

  get environmentSummary(): EnvironmentSummary[] {
    const counts = new Map<string, EnvironmentSummary>();
    this.versionRows.forEach(row => {
      const existing = counts.get(row.statusName);
      if (existing) {
        existing.count += 1;
      }
      else {
        counts.set(row.statusName, { name: row.statusName, count: 1, colorCode: row.colorCode });
      }
    });
    return [...counts.values()];
  }

  get featuredVersions(): FeaturedVersion[] {
    const development = this.findByStatusKeyword('develop') ?? null;
    const staging = this.findByStatusKeyword('lead site') ?? null;
    const latest = this.findByStatusKeyword('regional') ?? null;

    return [
      { key: 'latest', label: 'Latest Version', row: latest },
      { key: 'development', label: 'In Development', row: development },
      { key: 'staging', label: 'Staging', row: staging }
    ];
  }

  private findByStatusKeyword(keyword: string): VersionRow | undefined {
    const normalizedKeyword = keyword.toLowerCase();
    return this.versionRows.find(row => row.statusName.toLowerCase().includes(normalizedKeyword));
  }

  get myTicketsCount(): number {
    return this.ticketDashboard.cards[0]?.count ?? 0;
  }

  get cards(): DashboardCard[] {
    return this.ticketDashboard.cards;
  }

  get lists(): DashboardList[] {
    return this.ticketDashboard.lists;
  }

  get attentionCount(): number {
    return this.cards.find(card => this.cardVariant(card) === 'attention')?.count ?? 0;
  }

  get openItemsCount(): number {
    const openList = this.lists.find(list => /open/i.test(list.label));
    return openList ? openList.items.length : (this.lists[0]?.items.length ?? 0);
  }

  cardVariant(card: DashboardCard): CardVariant {
    const text = `${card.label} ${card.info}`.toLowerCase();
    if (/complet|closed/.test(text)) {
      return 'success';
    }
    if (/validat|uat/.test(text)) {
      return 'validation';
    }
    if (/attention|clarification|hold/.test(text)) {
      return 'attention';
    }
    return 'default';
  }

  isActivityList(list: DashboardList): boolean {
    return /recent activity/i.test(list.label);
  }

  private async loadTicketDashboard(useCaseId: number): Promise<void> {
    try {
      const response = await this.apiService.getFeedbackDashboard(useCaseId);
      const raw = response?.result ?? response;
      this.ticketDashboard = this.normalizeTicketDashboard(raw);
    }
    catch {
      this.ticketDashboard = this.createEmptyTicketDashboard();
    }
  }

  private normalizeTicketDashboard(raw: any): FeedbackDashboardData {
    const cards: DashboardCard[] = Array.isArray(raw?.cards)
      ? raw.cards.map((card: any) => {
          const rawCount = Number(card?.count);
          const count = Number.isFinite(rawCount) ? Math.max(0, Math.floor(rawCount)) : 0;
          return { label: card?.label ?? '', count, info: card?.info ?? '' };
        })
      : [];

    const lists: DashboardList[] = Array.isArray(raw?.lists)
      ? raw.lists.map((list: any) => ({
          label: list?.label ?? '',
          info: list?.info ?? '',
          items: Array.isArray(list?.items)
            ? list.items.map((item: any) => ({
                feedback_id: Number(item?.feedback_id) || 0,
                title: item?.title ?? ''
              }))
            : []
        }))
      : [];

    return { cards, lists };
  }

  private createEmptyTicketDashboard(): FeedbackDashboardData {
    return {
      cards: [],
      lists: []
    };
  }

  openCardTickets(card: DashboardCard, index: number): void {
    if (card.count <= 0) {
      return;
    }
    this.selectedCard = card;
    this.selectedCardIndex = index;
    this.drilldownTickets = [];
    this.drilldownPage = 1;
    this.drilldownTotalPages = 1;
    this.ticketDialog?.nativeElement.showModal();
    void this.loadDrilldownPage(1);
  }

  closeCardTickets(): void {
    this.ticketDialog?.nativeElement.close();
    this.selectedCard = null;
    this.selectedCardIndex = null;
    this.drilldownTickets = [];
  }

  goToDrilldownPage(page: number): void {
    if (page < 1 || page > this.drilldownTotalPages || page === this.drilldownPage || this.drilldownLoading) {
      return;
    }
    void this.loadDrilldownPage(page);
  }

  private async loadDrilldownPage(page: number): Promise<void> {
    const cardKey = this.selectedCardIndex !== null ? this.drilldownCardKeys[this.selectedCardIndex] : undefined;
    if (!cardKey) {
      return;
    }
    this.drilldownLoading = true;
    try {
      const response = await this.apiService.getFeedbackDashboardDrilldown(cardKey, page, this.drilldownRows);
      const raw = response?.result ?? response;
      this.drilldownTickets = Array.isArray(raw?.list)
        ? raw.list.map((item: any) => ({
            feedback_id: Number(item?.feedback_id) || 0,
            title: item?.title ?? ''
          }))
        : [];
      const [current, total] = String(raw?.page ?? `${page}/1`).split('/').map((part: string) => Number.parseInt(part, 10));
      this.drilldownPage = Number.isFinite(current) && current > 0 ? current : page;
      this.drilldownTotalPages = Number.isFinite(total) && total > 0 ? total : 1;
    }
    catch {
      this.drilldownTickets = [];
      this.drilldownPage = page;
      this.drilldownTotalPages = 1;
    }
    finally {
      this.drilldownLoading = false;
    }
  }

  onTicketDialogClick(event: MouseEvent): void {
    const dialog = this.ticketDialog?.nativeElement;
    if (!dialog || event.target !== dialog) {
      return;
    }
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom) {
      this.closeCardTickets();
    }
  }

  openTicketFromPopup(feedbackId: number): void {
    this.closeCardTickets();
    this.openTicketById(feedbackId);
  }

  openTicketById(feedbackId: number): void {
    this.router.navigate(['/support-hub/request/detail', feedbackId]);
  }

  openVersion(row: VersionRow | null): void {
    if (!row) {
      return;
    }

    this.router.navigate(['/support-hub/version'], {
      queryParams: this.store.getUseCaseQueryParams({ versionId: row.id })
    });
  }

}